class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.openTimer = null;
    this.previouslyFocused = null;
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-newsletter-popup-overlay]');
    this.dialog = this.querySelector('[data-newsletter-popup-dialog]');
    this.formPanel = this.querySelector('[data-newsletter-popup-form-panel]');
    this.successPanel = this.querySelector('[data-newsletter-popup-success-panel]');
    this.form = this.querySelector('form');
    this.closeButtons = this.querySelectorAll('[data-newsletter-popup-close], [data-newsletter-popup-continue]');

    this.storageKey = this.dataset.storageKey || 'nodus-newsletter-popup';
    this.pendingKey = `${this.storageKey}:pending`;
    this.delayMs = Math.max(0, Number(this.dataset.delay || 10) * 1000);
    this.showAgainDays = Math.max(1, Number(this.dataset.showAgainDays || 7));
    this.discountCode = (this.dataset.discountCode || '').trim();
    this.enabled = this.dataset.enabled !== 'false';
    this.pageHidden = this.dataset.pageHidden === 'true';
    this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify && Shopify.designMode);

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close({ markDismissed: !this.isSuccessView() }));
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', this.onOverlayClick);
    }

    if (this.form) {
      this.form.addEventListener('submit', () => this.markPending());
    }

    this.handleReturnState()
      .then((handledReturn) => {
        if (handledReturn) return;
        if (this.dataset.formError === 'true') {
          this.open({ success: false });
          return;
        }
        this.scheduleOpen();
      })
      .catch(() => {
        this.scheduleOpen();
      });
  }

  disconnectedCallback() {
    this.clearOpenTimer();
    document.removeEventListener('keydown', this.onKeyDown);
    document.body.classList.remove('overflow-hidden');
  }

  scheduleOpen() {
    if (!this.enabled && !this.designMode) return;

    this.clearOpenTimer();

    if (this.designMode) {
      this.open({ success: false });
      return;
    }

    if (this.pageHidden) return;
    if (this.isSubscribed()) return;
    if (!this.shouldShowAfterDismiss()) return;

    this.openTimer = window.setTimeout(() => {
      this.open({ success: false });
    }, this.delayMs);
  }

  clearOpenTimer() {
    if (this.openTimer) {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  async handleReturnState() {
    const params = new URLSearchParams(window.location.search);
    const subscribedParam =
      params.get('newsletter_subscribed') === '1' || params.get('customer_posted') === 'true';
    const formSuccess = this.dataset.formSuccess === 'true';
    const pending = this.isPending();

    // Claim return only for this popup (pending submit) or Liquid success on this form.
    if (!formSuccess && !(pending && subscribedParam)) return false;

    this.clearPending();
    this.markSubscribed();
    this.cleanReturnUrl();

    await this.applyDiscountCode();
    this.open({ success: true });
    return true;
  }

  open({ success = false } = {}) {
    if (!this.dialog || !this.overlay) return;

    this.clearOpenTimer();
    this.previouslyFocused = document.activeElement;
    this.setSuccessView(success);

    this.overlay.hidden = false;
    this.dialog.hidden = false;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeyDown);

    window.requestAnimationFrame(() => {
      // Focus dialog/close — not the email input (avoids native "required" tooltip).
      const focusTarget = success
        ? this.querySelector('[data-newsletter-popup-continue]') || this.dialog
        : this.querySelector('[data-newsletter-popup-close]') || this.dialog;

      if (typeof trapFocus === 'function') {
        trapFocus(this.dialog, focusTarget);
      } else if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    });
  }

  close({ markDismissed = true } = {}) {
    if (!this.dialog || !this.overlay) return;

    const wasSuccess = this.isSuccessView();

    this.classList.remove('is-open');
    this.overlay.hidden = true;
    this.dialog.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeyDown);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.previouslyFocused);
    } else if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }

    if (wasSuccess) {
      this.markSubscribed();
      return;
    }

    if (markDismissed) {
      this.markDismissed();
    }
  }

  setSuccessView(success) {
    if (this.formPanel) {
      this.formPanel.hidden = Boolean(success);
      this.formPanel.classList.toggle('hidden', Boolean(success));
    }

    if (this.successPanel) {
      this.successPanel.hidden = !success;
      this.successPanel.classList.toggle('hidden', !success);
    }

    this.classList.toggle('is-success', Boolean(success));

    if (this.dialog) {
      const headingId = success
        ? this.successPanel?.querySelector('.newsletter-popup__heading')?.id
        : this.formPanel?.querySelector('.newsletter-popup__heading')?.id;
      if (headingId) this.dialog.setAttribute('aria-labelledby', headingId);
    }
  }

  isSuccessView() {
    return this.classList.contains('is-success');
  }

  onKeyDown(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault();
      this.close({ markDismissed: !this.isSuccessView() });
    }
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close({ markDismissed: !this.isSuccessView() });
    }
  }

  getStorageRecord() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (raw) return JSON.parse(raw);
    } catch (error) {
      // Ignore storage failures and fall back to cookie.
    }

    const cookie = this.readCookie(this.storageKey);
    if (!cookie) return null;

    try {
      return JSON.parse(decodeURIComponent(cookie));
    } catch (error) {
      if (cookie === 'subscribed') return { status: 'subscribed', ts: Date.now() };
      return null;
    }
  }

  writeStorageRecord(record) {
    const payload = JSON.stringify(record);
    try {
      window.localStorage.setItem(this.storageKey, payload);
    } catch (error) {
      // Ignore quota / private mode.
    }

    const maxAge =
      record.status === 'subscribed'
        ? 60 * 60 * 24 * 365
        : 60 * 60 * 24 * this.showAgainDays;

    this.writeCookie(this.storageKey, encodeURIComponent(payload), maxAge);
  }

  isSubscribed() {
    const record = this.getStorageRecord();
    return Boolean(record && record.status === 'subscribed');
  }

  shouldShowAfterDismiss() {
    const record = this.getStorageRecord();
    if (!record || record.status !== 'dismissed') return true;
    const ts = Number(record.ts || 0);
    if (!ts) return true;
    const elapsed = Date.now() - ts;
    return elapsed >= this.showAgainDays * 24 * 60 * 60 * 1000;
  }

  markSubscribed() {
    this.writeStorageRecord({ status: 'subscribed', ts: Date.now() });
  }

  markDismissed() {
    this.writeStorageRecord({ status: 'dismissed', ts: Date.now() });
  }

  markPending() {
    try {
      window.sessionStorage.setItem(this.pendingKey, '1');
    } catch (error) {
      // Ignore.
    }
    this.writeCookie(this.pendingKey, '1', 60 * 10);
  }

  isPending() {
    try {
      if (window.sessionStorage.getItem(this.pendingKey) === '1') return true;
    } catch (error) {
      // Ignore.
    }
    return this.readCookie(this.pendingKey) === '1';
  }

  clearPending() {
    try {
      window.sessionStorage.removeItem(this.pendingKey);
    } catch (error) {
      // Ignore.
    }
    this.writeCookie(this.pendingKey, '', -1);
  }

  cleanReturnUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('newsletter_subscribed');
    url.searchParams.delete('customer_posted');
    url.searchParams.delete('form_type');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  }

  async applyDiscountCode() {
    if (!this.discountCode) return;

    try {
      const existing = await this.getExistingDiscountCodes();
      const combined = existing.slice();
      if (!combined.some((code) => code.toLowerCase() === this.discountCode.toLowerCase())) {
        combined.push(this.discountCode);
      }

      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: combined.join(','),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;
      const cart = await response.json();
      if (cart && cart.sections) {
        this.refreshCartSections(cart.sections);
      }
    } catch (error) {
      // Discount apply is best-effort; success UI still shows.
    }
  }

  async getExistingDiscountCodes() {
    const holder = document.querySelector('[data-cart-applied-codes]');
    if (holder) {
      const raw = holder.getAttribute('data-cart-applied-codes') || '';
      return raw
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean);
    }

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return [];
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.discount_codes)) return [];
      return cart.discount_codes
        .map((entry) => (entry && entry.code ? String(entry.code).trim() : ''))
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  refreshCartSections(sections) {
    if (!sections) return;

    const drawerHtml = sections['cart-drawer'];
    if (drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const nextInner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
      const currentInner = document.querySelector('#CartDrawer .drawer__inner');
      if (nextInner && currentInner) {
        currentInner.innerHTML = nextInner.innerHTML;
      }
    }

    const bubbleHtml = sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const nextBubble = doc.querySelector('.shopify-section') || doc.body;
      const bubble = document.getElementById('cart-icon-bubble');
      if (nextBubble && bubble) {
        bubble.innerHTML = nextBubble.innerHTML;
      }
    }

    if (typeof window.updateCartDrawerShippingBars === 'function') {
      window.updateCartDrawerShippingBars();
    }
  }

  writeCookie(name, value, maxAgeSeconds) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  }

  readCookie(name) {
    const prefix = `${name}=`;
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0; i < parts.length; i += 1) {
      if (parts[i].indexOf(prefix) === 0) {
        return parts[i].slice(prefix.length);
      }
    }
    return null;
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
